import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, message, Modal, Tag } from 'antd';
import { DeleteOutlined, ReloadOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import NewButton from '../../../components/NewButton/NewButton';
import ConfirmDialog from '../../../components/ConfirmDialog/ConfirmDialog';
import {
  getAllConfigurationComparisons,
  deleteConfigurationComparison,
  createBulkCompare,
  getAllClientEnvironments,
  rerunComparison,
} from '../../../infrastructure/apiClient';
import type {
  ClientEnvironmentDTO,
  ConfigurationComparisonDTO,
} from '../../../infrastructure/models';
import CardTable from '../../../components/Table/CardTable';
import { buildFilterOptions, getCheckboxFilterProps } from '../../../components/Table/columnFilterUtils';
import DashboardLayout, { dashboardStyles as styles } from '../../../components/DashboardLayout/DashboardLayout';
import { getNextPageAfterDelete } from '../../../utils/pagination';
import { useDashboardData } from '../../../hooks/useDashboardData';
import { useDeleteConfirmation } from '../../../hooks/useDeleteConfirmation';
import { sortData, sortConfig, type SortConfig } from '../../../utils/sortUtils';
import { buildEnvironmentNameMap, getEnvironmentName } from '../../../utils/environmentUtils';
import ConfigurationComparison, { type ConfigurationComparisonPayload } from '../ConfigurationComparison/ConfigurationComparison';

interface ConfigurationComparisonDashboardProps {
  onBack?: () => void;
  onComparisonSelect?: (comparison: ConfigurationComparisonDTO) => void;
}

const STATUS_COLORS: Record<string, string> = {
  Pending: 'gold',
  Completed: 'green',
  Failed: 'red',
};

const getSelectionsLabel = (record: ConfigurationComparisonDTO) =>
  record.comparisonMode === 'SelectedFolders' && record.selections?.length
    ? record.selections.join(', ')
    : '-';

const SORT_CONFIGS: SortConfig<ConfigurationComparisonDTO>[] = [
  sortConfig('name', 'string'),
  sortConfig('description', 'string'),
  sortConfig('environmentA', 'string'),
  sortConfig('fileA', 'string'),
  sortConfig('environmentB', 'string'),
  sortConfig('fileB', 'string'),
  sortConfig('comparisonMode', 'string'),
  sortConfig('selections', 'string', getSelectionsLabel),
  sortConfig('status', 'string'),
  sortConfig('createdBy', 'string'),
  sortConfig('createdAt', 'date'),
  sortConfig('updatedAt', 'date'),
];

const ConfigurationComparisonDashboard = ({ onBack, onComparisonSelect }: ConfigurationComparisonDashboardProps) => {
  const {
    data: comparisons,
    loading,
    setLoading,
    error,
    activeClient,
    tenantsLoading,
    pagination,
    sorter,
    tablePagination,
    handleTableChange,
    updateCurrentPage,
    fetchData: fetchComparisons,
    abortControllerRef,
    disableActions,
  } = useDashboardData<ConfigurationComparisonDTO>({
    fetchFn: getAllConfigurationComparisons,
    entityName: 'configuration comparisons',
  });
  const [comparisonModalOpen, setComparisonModalOpen] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [environments, setEnvironments] = useState<ClientEnvironmentDTO[]>([]);
  const [environmentsLoading, setEnvironmentsLoading] = useState(false);

  const [nameFilters, setNameFilters] = useState<string[]>([]);
  const [descriptionFilters, setDescriptionFilters] = useState<string[]>([]);
  const [srcEnvFilters, setSrcEnvFilters] = useState<string[]>([]);
  const [srcFileFilters, setSrcFileFilters] = useState<string[]>([]);
  const [tgtEnvFilters, setTgtEnvFilters] = useState<string[]>([]);
  const [tgtFileFilters, setTgtFileFilters] = useState<string[]>([]);
  const [modeFilters, setModeFilters] = useState<string[]>([]);
  const [selectionsFilters, setSelectionsFilters] = useState<string[]>([]);
  const [statusFilters, setStatusFilters] = useState<string[]>([]);
  const [srcRecordsFilters, setSrcRecordsFilters] = useState<string[]>([]);
  const [tgtRecordsFilters, setTgtRecordsFilters] = useState<string[]>([]);
  const [matchingFilters, setMatchingFilters] = useState<string[]>([]);

  const environmentMap = useMemo(() => buildEnvironmentNameMap(environments), [environments]);

  useEffect(() => {
    if (!activeClient?.id) {
      setEnvironments([]);
      return;
    }
    const controller = new AbortController();
    setEnvironmentsLoading(true);
    getAllClientEnvironments(activeClient.id, { offset: 0, limit: 1000, signal: controller.signal })
      .then((response) => {
        if (!controller.signal.aborted) {
          setEnvironments(response?.data ?? []);
        }
      })
      .catch((err) => {
        if (!controller.signal.aborted) {
          console.error('Failed to load environments', err);
          setEnvironments([]);
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setEnvironmentsLoading(false);
        }
      });
    return () => controller.abort();
  }, [activeClient?.id]);

  const handleDeleteComparison = useCallback(
    async (comparison: ConfigurationComparisonDTO) => {
      if (!activeClient?.id) {
        message.warning('Select a tenant before deleting a comparison.');
        return;
      }

      try {
        setLoading(true);
        await deleteConfigurationComparison(activeClient.id, comparison.id);
        const { nextPage, offset } = getNextPageAfterDelete({
          currentPage: pagination.current,
          pageSize: pagination.pageSize,
          totalItems: pagination.total,
          deletedItems: 1,
        });
        updateCurrentPage(nextPage);
        abortControllerRef.current?.abort();
        const controller = new AbortController();
        abortControllerRef.current = controller;
        await fetchComparisons(activeClient.id, {
          offset,
          limit: pagination.pageSize,
          signal: controller.signal,
        });
        message.success(`Comparison "${comparison.name}" deleted.`);
      } catch (err) {
        console.error('Failed to delete comparison', err);
        message.error('Failed to delete comparison.');
      } finally {
        setLoading(false);
      }
    },
    [activeClient?.id, fetchComparisons, pagination.current, pagination.pageSize, pagination.total, updateCurrentPage],
  );

  const {
    confirmOpen,
    confirmLoading,
    itemToDelete: confirmComparison,
    requestDelete: handleOpenDeleteConfirm,
    cancelDelete: handleCloseDeleteConfirm,
    confirmDelete: handleConfirmDelete,
  } = useDeleteConfirmation<ConfigurationComparisonDTO>(handleDeleteComparison);

  const nameOptions = useMemo(() => buildFilterOptions(comparisons, (r) => r.name ?? undefined), [comparisons]);
  const descriptionOptions = useMemo(() => buildFilterOptions(comparisons, (r) => r.description ?? undefined), [comparisons]);
  const srcEnvOptions = useMemo(() => buildFilterOptions(comparisons, (r) => getEnvironmentName(r.environmentA, environmentMap) || undefined), [comparisons, environmentMap]);
  const srcFileOptions = useMemo(() => buildFilterOptions(comparisons, (r) => r.fileA ?? undefined), [comparisons]);
  const tgtEnvOptions = useMemo(() => buildFilterOptions(comparisons, (r) => getEnvironmentName(r.environmentB, environmentMap) || undefined), [comparisons, environmentMap]);
  const tgtFileOptions = useMemo(() => buildFilterOptions(comparisons, (r) => r.fileB ?? undefined), [comparisons]);
  const modeOptions = useMemo(() => buildFilterOptions(comparisons, (r) => r.comparisonMode ?? undefined), [comparisons]);
  const selectionsOptions = useMemo(() => buildFilterOptions(comparisons, (r) => { const label = getSelectionsLabel(r); return label !== '-' ? label : undefined; }), [comparisons]);
  const statusOptions = useMemo(() => buildFilterOptions(comparisons, (r) => r.status ?? undefined), [comparisons]);
  const srcRecordsOptions = useMemo(() => buildFilterOptions(comparisons, (r) => r.summary?.totalNodesInA == null ? undefined : String(r.summary.totalNodesInA)), [comparisons]);
  const tgtRecordsOptions = useMemo(() => buildFilterOptions(comparisons, (r) => r.summary?.totalNodesInB == null ? undefined : String(r.summary.totalNodesInB)), [comparisons]);
  const matchingOptions = useMemo(() => buildFilterOptions(comparisons, (r) => r.summary?.nodesMatching == null ? undefined : String(r.summary.nodesMatching)), [comparisons]);

  const columns = useMemo<ColumnsType<ConfigurationComparisonDTO>>(
    () => [
      {
        title: 'Name',
        dataIndex: 'name',
        key: 'name',
        ellipsis: true,
        sorter: (a, b) => a.name.localeCompare(b.name),
        sortOrder: sorter.columnKey === 'name' ? sorter.order : null,
        ...getCheckboxFilterProps(nameFilters, setNameFilters, nameOptions, (r) => r.name ?? undefined),
        render: (value: string, record) => {
          const status = (record.status || '').toLowerCase();
          const isCompleted = status === 'completed';
          if (!isCompleted) {
            return value;
          }
          return (
            <button
              type="button"
              className={styles.nameLink}
              onClick={() => onComparisonSelect?.(record)}
            >
              {value}
            </button>
          );
        },
      },
      {
        title: 'Source',
        dataIndex: 'environmentA',
        key: 'environmentA',
        ellipsis: true,
        sorter: (a, b) => getEnvironmentName(a.environmentA, environmentMap).localeCompare(getEnvironmentName(b.environmentA, environmentMap)),
        sortOrder: sorter.columnKey === 'environmentA' ? sorter.order : null,
        ...getCheckboxFilterProps(srcEnvFilters, setSrcEnvFilters, srcEnvOptions, (r) => getEnvironmentName(r.environmentA, environmentMap) || undefined),
        render: (value: string) => getEnvironmentName(value, environmentMap),
      },
      {
        title: 'Target',
        dataIndex: 'environmentB',
        key: 'environmentB',
        ellipsis: true,
        sorter: (a, b) => getEnvironmentName(a.environmentB, environmentMap).localeCompare(getEnvironmentName(b.environmentB, environmentMap)),
        sortOrder: sorter.columnKey === 'environmentB' ? sorter.order : null,
        ...getCheckboxFilterProps(tgtEnvFilters, setTgtEnvFilters, tgtEnvOptions, (r) => getEnvironmentName(r.environmentB, environmentMap) || undefined),
        render: (value: string) => getEnvironmentName(value, environmentMap),
      },
      {
        title: 'Folders Compared',
        dataIndex: ['summary', 'totalFoldersCompared'],
        key: 'totalFoldersCompared',
        sorter: (a, b) => (a.summary?.totalFoldersCompared ?? 0) - (b.summary?.totalFoldersCompared ?? 0),
        sortOrder: sorter.columnKey === 'totalFoldersCompared' ? sorter.order : null,
        render: (_value: unknown, record: ConfigurationComparisonDTO) =>
          record.summary?.totalFoldersCompared ?? '—',
      },
      {
        title: 'Folders Matching',
        dataIndex: ['summary', 'foldersMatching'],
        key: 'foldersMatching',
        sorter: (a, b) => (a.summary?.foldersMatching ?? 0) - (b.summary?.foldersMatching ?? 0),
        sortOrder: sorter.columnKey === 'foldersMatching' ? sorter.order : null,
        render: (_value: unknown, record: ConfigurationComparisonDTO) =>
          record.summary?.foldersMatching ?? '—',
      },
      {
        title: '% Folders Matched',
        key: 'pctFoldersMatched',
        sorter: (a, b) => {
          const pctA = a.summary?.totalFoldersCompared ? (a.summary.foldersMatching / a.summary.totalFoldersCompared) * 100 : 0;
          const pctB = b.summary?.totalFoldersCompared ? (b.summary.foldersMatching / b.summary.totalFoldersCompared) * 100 : 0;
          return pctA - pctB;
        },
        sortOrder: sorter.columnKey === 'pctFoldersMatched' ? sorter.order : null,
        render: (_value: unknown, record: ConfigurationComparisonDTO) => {
          const { totalFoldersCompared, foldersMatching } = record.summary ?? {};
          if (totalFoldersCompared == null || totalFoldersCompared === 0) return '—';
          return `${Math.round((foldersMatching! / totalFoldersCompared) * 100)}%`;
        },
      },
      {
        title: '% Items Matched',
        key: 'pctItemsMatched',
        sorter: (a, b) => {
          const totalA = Math.max(a.summary?.totalNodesInA ?? 0, a.summary?.totalNodesInB ?? 0);
          const totalB = Math.max(b.summary?.totalNodesInA ?? 0, b.summary?.totalNodesInB ?? 0);
          const pctA = totalA ? ((a.summary?.nodesMatching ?? 0) / totalA) * 100 : 0;
          const pctB = totalB ? ((b.summary?.nodesMatching ?? 0) / totalB) * 100 : 0;
          return pctA - pctB;
        },
        sortOrder: sorter.columnKey === 'pctItemsMatched' ? sorter.order : null,
        render: (_value: unknown, record: ConfigurationComparisonDTO) => {
          const { totalNodesInA, totalNodesInB, nodesMatching } = record.summary ?? {};
          const total = Math.max(totalNodesInA ?? 0, totalNodesInB ?? 0);
          if (!total) return '—';
          return `${Math.round((nodesMatching! / total) * 100)}%`;
        },
      },
      {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
        sorter: (a, b) => a.status.localeCompare(b.status),
        sortOrder: sorter.columnKey === 'status' ? sorter.order : null,
        ...getCheckboxFilterProps(statusFilters, setStatusFilters, statusOptions, (r) => r.status ?? undefined),
        render: (status: string) => (
          <Tag color={STATUS_COLORS[status] || 'default'} className="!mr-0">
            {status}
          </Tag>
        ),
      },      {
        title: 'Run By',
        dataIndex: 'createdBy',
        key: 'createdBy',
        ellipsis: true,
        sorter: (a, b) => (a.createdBy ?? '').localeCompare(b.createdBy ?? ''),
        sortOrder: sorter.columnKey === 'createdBy' ? sorter.order : null,
        render: (value: string, record) => {
          const isFailed = (record.status || '').toLowerCase() === 'failed';
          return (
            <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {value || '—'}
              {isFailed && (
                <button
                  type="button"
                  className={styles.nameLink}
                  aria-label="Retry comparison"
                  onClick={async () => {
                    if (!activeClient?.id) return;
                    try {
                      await rerunComparison(activeClient.id, record.id);
                      message.success(`Comparison "${record.name}" resubmitted.`);
                      abortControllerRef.current?.abort();
                      const controller = new AbortController();
                      abortControllerRef.current = controller;
                      await fetchComparisons(activeClient.id, {
                        offset: 0,
                        limit: pagination.pageSize,
                        signal: controller.signal,
                      });
                    } catch (err) {
                      console.error('Failed to retry comparison', err);
                      message.error('Failed to retry comparison.');
                    }
                  }}
                  disabled={loading || tenantsLoading || !activeClient?.id}
                >
                  <ReloadOutlined />
                </button>
              )}
            </span>
          );
        },
      },
      {
        title: 'Delete',
        key: 'delete',
        align: 'center',
        width: 80,
        render: (_value, record) => (
          <button
            type="button"
            className={styles.deleteButton}
            aria-label="Delete comparison"
            onClick={() => handleOpenDeleteConfirm(record)}
            disabled={loading || tenantsLoading || !activeClient?.id}
          >
            <DeleteOutlined />
          </button>
        ),
      },
    ],
    [
      sorter.columnKey,
      sorter.order,
      onComparisonSelect,
      handleOpenDeleteConfirm,
      loading,
      tenantsLoading,
      activeClient?.id,
      environmentMap,
      nameFilters, nameOptions, descriptionFilters, descriptionOptions,
      srcEnvFilters, srcEnvOptions, srcFileFilters, srcFileOptions,
      tgtEnvFilters, tgtEnvOptions, tgtFileFilters, tgtFileOptions,
      modeFilters, modeOptions, selectionsFilters, selectionsOptions,
      statusFilters, statusOptions,
      srcRecordsFilters, srcRecordsOptions, tgtRecordsFilters, tgtRecordsOptions,
      matchingFilters, matchingOptions,
    ],
  );

  const sortedComparisons = useMemo(
    () => sortData(comparisons, sorter, SORT_CONFIGS),
    [comparisons, sorter],
  );

  return (
    <DashboardLayout
      title="Configuration Comparison"
      actions={

        <div className="flex gap-2">
         
          <NewButton
            onClick={() => setComparisonModalOpen(true)}
            disabled={disableActions}
            label="New Configuration Comparison"
          />
          {onBack && (
            <Button onClick={onBack}>
              Back to Configuration Uploads
            </Button>
          )}

           <Button
            icon={<ReloadOutlined />}
            onClick={async () => {
              if (!activeClient?.id) return;
              abortControllerRef.current?.abort();
              const controller = new AbortController();
              abortControllerRef.current = controller;
              setRefreshing(true);
              try {
                await fetchComparisons(activeClient.id, {
                  offset: 0,
                  limit: pagination.pageSize,
                  signal: controller.signal,
                });
              } finally {
                if (!controller.signal.aborted) {
                  setRefreshing(false);
                }
              }
            }}
            disabled={disableActions || refreshing}
            loading={refreshing}
          >
            Refresh
          </Button>
        </div>
      }
    >
      <CardTable<ConfigurationComparisonDTO>
        title="Configuration Comparisons"
        columns={columns}
        dataSource={sortedComparisons}
        rowKey={(record) => record.id}
        loading={loading || tenantsLoading}
        pagination={tablePagination}
        onChange={handleTableChange}
        error={error}
        emptyText={
          disableActions
            ? 'Select a tenant to view configuration comparisons.'
            : 'No configuration comparisons found yet.'
        }
        className={styles.cardTable}
      />

      <ConfirmDialog
        open={confirmOpen}
        title="Delete comparison"
        message={
          confirmComparison
            ? `Are you sure you want to delete the comparison "${confirmComparison.name}"?`
            : ''
        }
        confirmText="Delete"
        cancelText="Cancel"
        confirmLoading={confirmLoading}
        onConfirm={handleConfirmDelete}
        onCancel={handleCloseDeleteConfirm}
      />

      <Modal
        open={comparisonModalOpen}
        title="New Configuration Comparison"
        onCancel={() => setComparisonModalOpen(false)}
        footer={null}
        destroyOnHidden
        width={780}
        styles={{ body: { minHeight: 400 } }}
      >
        <ConfigurationComparison
          environments={environments}
          environmentsLoading={environmentsLoading}
          onCancel={() => setComparisonModalOpen(false)}
          onCompare={async (payload: ConfigurationComparisonPayload) => {
            if (!activeClient?.id) {
              message.warning('Select a tenant before comparing.');
              return;
            }
            try {
              setLoading(true);
              await createBulkCompare(activeClient.id, {
                name: payload.name,
                description: payload.description,
                environmentA: payload.environmentA,
                fileA: payload.fileA,
                environmentB: payload.environmentB,
                fileB: payload.fileB,
                mode: payload.mode,
                selections: payload.selections,
              });
              abortControllerRef.current?.abort();
              const controller = new AbortController();
              abortControllerRef.current = controller;
              await fetchComparisons(activeClient.id, {
                offset: 0,
                limit: pagination.pageSize,
                signal: controller.signal,
              });
              message.success(`Comparison "${payload.comparisonName}" created.`);
              setComparisonModalOpen(false);
            } catch (err) {
              console.error('Failed to create comparison', err);
              message.error('Failed to create comparison.');
            } finally {
              setLoading(false);
            }
          }}
        />
      </Modal>
    </DashboardLayout>
  );
};

export default ConfigurationComparisonDashboard;
